/*
  OFFSET add-to-cart confetti. Xavier, 27 Sep 2026.

  No library, no app. One canvas, about 4KB of source, and nothing at all runs
  until somebody actually adds something to their cart. That is the whole
  performance story: the file is deferred, the canvas is not created on page
  load, and the render loop ends itself and releases the canvas when the last
  piece leaves the screen.

  What fires it. Exactly one signal: `cart:change` carrying
  `baseEvent: 'variant:add'`. The theme's product form, the cross sell and the
  drawer essentials all dispatch that, so all three add paths are covered. The
  things that write to the cart WITHOUT it stay silent on purpose, and that
  matters: the reward ladder dropping in a free gift, the shipping protection
  switch, a quantity going down, a plain redraw. Confetti has to mean "you
  added something", or it means nothing.

  Why paper strips and not dots. Dots read as a free plugin. These are thin
  rectangles that spin on two axes, so each one flattens to a line and opens
  back out as it turns, the way real foil confetti catches light. The palette
  is the OFFSET blue with its own tints plus white and a silver, no rainbow.

  One cannon at the centre of the bottom edge, fired in a wide fan. The pieces
  open outwards as they rise, then fall down the sides. Deliberately not a row
  of launch points along the bottom, which reads as a stripe rather than a
  burst.

  Respects prefers-reduced-motion by doing nothing.
*/
(function () {
  'use strict';

  var COLORS = [
    '#0017E8',  // OFFSET blue, the anchor
    '#0017E8',
    '#2F45FF',  // a brighter tint, so the mass is not flat
    '#6B7CFF',
    '#B9C3FF',  // pale, reads as light catching the foil
    '#FFFFFF',
    '#FFFFFF',
    '#C8CEDE'   // silver, the premium note
  ];

  var canvas = null, ctx = null, pieces = [], raf = 0, dpr = 1, lastFired = 0;

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function size() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function mount() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    // 1000 clears the cart drawer, which sits at 999. pointer-events none so
    // it can never swallow a tap on the checkout button underneath it.
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;z-index:1000;pointer-events:none;';
    ctx = canvas.getContext('2d');
    document.body.appendChild(canvas);
    size();
    window.addEventListener('resize', size);
  }

  function unmount() {
    window.removeEventListener('resize', size);
    if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
    canvas = null; ctx = null; pieces = [];
  }

  function rand(a, b) { return a + Math.random() * (b - a); }

  function burst() {
    var w = window.innerWidth, h = window.innerHeight;
    var count = w < 700 ? 150 : 260;

    for (var i = 0; i < count; i++) {
      /* One cannon in the middle, not a row of them along the bottom. Every
         piece starts within a narrow band at the centre and is thrown out on
         an angle, so it reads as a single burst that opens into a fan and
         then rains down the sides. That is the shape that was asked for.

         The angle is measured from straight up and fanned to either side.
         Speed varies a lot on purpose: the slow ones stay near the middle,
         the fast ones reach the edges, and the spread fills in between. */
      var x = w * 0.5 + rand(-w * 0.06, w * 0.06);
      var angle = rand(-1.02, 1.02);              // about 58 degrees each way

      /* The two axes are scaled to DIFFERENT dimensions, which matters more
         than it sounds. Height decides how high it climbs. Width decides how
         far it fans. Scaling both to height, as the first version did, threw
         every piece clean off the sides of a phone within half a second and
         left about ten on screen. */
      var rise = (h * 0.034) * rand(0.5, 1.05);
      var fan  = (w * 0.032) * rand(0.45, 1.15);
      var ribbon = Math.random() < 0.26;

      pieces.push({
        x: x,
        y: h + rand(4, 26),
        vx: Math.sin(angle) * fan,
        vy: -Math.abs(Math.cos(angle)) * rise,
        w: ribbon ? rand(2.5, 4) : rand(6, 11),
        h: ribbon ? rand(14, 22) : rand(8, 14),
        color: COLORS[(Math.random() * COLORS.length) | 0],
        // two axes: spin is the tumble, tilt is what flattens it to a line
        spin: rand(-0.19, 0.19),
        angle: rand(0, Math.PI * 2),
        tilt: rand(0, Math.PI * 2),
        tiltSpeed: rand(0.045, 0.11),
        drift: rand(0.004, 0.014),
        phase: rand(0, Math.PI * 2)
      });
    }
  }

  function frame() {
    var h = window.innerHeight, w = window.innerWidth, alive = 0;
    ctx.clearRect(0, 0, w, h);

    for (var i = 0; i < pieces.length; i++) {
      var p = pieces[i];

      /* Two different speeds on purpose, because that is what paper does and
         it is what was asked for: a fast pop up, then a slow drift down.
         Gravity is strong enough to turn it over in about eight tenths of a
         second, and then TERMINAL caps the fall so it never builds up speed.
         Without that cap the pieces accelerate and it looks like debris. */
      p.vy += h * 0.0006;
      var terminal = h * 0.0062;
      if (p.vy > terminal) p.vy = terminal;
      /* Hard horizontal drag, soft vertical. A real cannon throws the paper
         out fast, the air kills the sideways speed in under a second, and
         then it just falls. 0.992 barely slowed anything down, so the fan
         kept opening until everything was off screen. */
      p.vx *= 0.95;
      p.phase += p.drift;
      // The sideways wander only really shows on the way down, once the
      // upward speed has bled off. Tying it to the fall keeps the launch
      // looking like a launch rather than a wobble.
      var sway = p.vy > 0 ? 1.5 : 0.5;
      p.x += p.vx + Math.sin(p.phase) * sway;
      p.y += p.vy;
      p.angle += p.spin;
      p.tilt += p.tiltSpeed;

      // Gone once it is under the screen and still heading down. A piece that
      // starts below the edge on the way UP has not had its turn yet.
      if (p.y - 30 > h && p.vy > 0) continue;
      alive++;

      // Fade the last stretch so nothing snaps out of existence.
      var fade = p.y > h - 90 && p.vy > 0 ? Math.max(0, (h + 30 - p.y) / 120) : 1;

      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      // cos of the tilt squashes the width: the piece turns edge-on and back
      ctx.scale(Math.cos(p.tilt), 1);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }

    if (alive > 0) {
      raf = requestAnimationFrame(frame);
    } else {
      raf = 0;
      unmount();
    }
  }

  function fire() {
    if (reduced() || document.hidden) return;
    var now = Date.now();
    if (now - lastFired < 700) return;   // adding three things fast is one party
    lastFired = now;

    mount();
    burst();
    if (!raf) raf = requestAnimationFrame(frame);
  }

  document.addEventListener('cart:change', function (event) {
    var d = event && event.detail;
    if (d && d.baseEvent === 'variant:add') fire();
  });

  // Leaving the tab mid-flight would otherwise keep a stalled canvas around.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden && raf) { cancelAnimationFrame(raf); raf = 0; unmount(); }
  });
})();
